import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, InputGroup, Pagination, OverlayTrigger, Tooltip, Modal, Spinner, Alert } from 'react-bootstrap';
import { FaSearch, FaPlus, FaEye, FaCheckCircle, FaBan, FaAngleLeft, FaAngleRight, FaExclamationTriangle, FaUsers, FaUserFriends } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { rmApi } from '../../services/api';

const RMList = () => {
  const navigate = useNavigate();
  
  // State variables
  const [searchTerm, setSearchTerm] = useState('');
  const [rmList, setRmList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedRm, setSelectedRm] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  
  // Function to fetch RMs from API
  const fetchRMs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Calculate skip from current page and entries per page
      const skip = (currentPage - 1) * entriesPerPage;
      
      const response = await rmApi.getRelationshipManagers({
        skip,
        limit: entriesPerPage,
        search: searchTerm || undefined
      });
      
      setRmList(response.data.items);
      setTotalItems(response.data.total);
      setTotalPages(Math.ceil(response.data.total / entriesPerPage));
    } catch (err) {
      console.error('Error fetching relationship managers:', err);
      setError('Failed to fetch relationship managers. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch RMs on initial render and when search/pagination parameters change
  useEffect(() => {
    fetchRMs();
  }, [currentPage, entriesPerPage]);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      // Reset to first page when search term changes
      setCurrentPage(1);
      fetchRMs();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Modal handlers
  const openDeactivateModal = (rm) => {
    setSelectedRm(rm);
    setShowDeactivateModal(true);
  };
  
  const handleDeactivate = async () => {
    if (!selectedRm) return;
    
    setStatusUpdateLoading(true);
    try {
      // Call the API to toggle the RM status
      const newStatus = selectedRm.status === 'Active' ? false : true;
      await rmApi.toggleRmStatus(selectedRm.id, newStatus);
      
      // Close the modal
      setShowDeactivateModal(false);
      
      // Refresh the RM list
      fetchRMs();
    } catch (err) {
      console.error('Error toggling RM status:', err);
      setError(`Failed to ${selectedRm.status === 'Active' ? 'deactivate' : 'activate'} the relationship manager.`);
    } finally {
      setStatusUpdateLoading(false);
    }
  };
  
  // Function to navigate to details page
  const viewDetails = (id) => {
    navigate(`/admin/edit-rm/${id}`);
  };
  
  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }
  
  // Limit visible page numbers (show max 3 pages with prev/next)
  const getVisiblePageNumbers = () => {
    if (totalPages <= 3) {
      return pageNumbers;
    }
    
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    
    return [currentPage - 1, currentPage, currentPage + 1];
  };
  
  // Calculate current entry range for display
  const indexOfFirstEntry = (currentPage - 1) * entriesPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * entriesPerPage, totalItems);

  return (
    <Container fluid className="p-3">
      {/* Page Header */}
      <Row className="mb-2">
        <Col>
          <h4 className="mb-1">RM List</h4>
          <p className="text-muted small mb-1">Manage Relationship Managers in the system</p>
        </Col>
      </Row>
      
      {/* Error Alert */}
      {error && (
        <Row className="mb-2">
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}
      
      {/* Search Bar and Add Button */}
      <Row className="mb-2">
        <Col md={4} lg={4}>
          <InputGroup className="mb-2">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search by name, email or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button 
            variant="primary"
            onClick={() => navigate('/admin/add-rm')}
          >
            <FaPlus className="me-2" /> Add New RM
          </Button>
        </Col>
      </Row>

      {/* RM List Table */}
      <Row>
        <Col>
          <Card>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading relationship managers...</p>
                </div>
              ) : (
                <>
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Location</th>
                        <th>DSOs</th>
                        <th>Agents</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rmList.length > 0 ? (
                        rmList.map((rm) => (
                          <tr 
                            key={rm.id}
                            className={rm.status === 'Inactive' ? 'table-danger' : ''}
                          >
                            <td>{rm.id}</td>
                            <td>
                              <a 
                                href="#" 
                                onClick={(e) => {e.preventDefault(); viewDetails(rm.id);}}
                                className="text-decoration-none"
                                style={{ cursor: 'pointer', color: '#4169E1' }}
                              >
                                {rm.name}
                              </a>
                            </td>
                            <td>{rm.email}</td>
                            <td>{rm.phone}</td>
                            <td>{rm.location}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <FaUsers className="me-1 text-primary" /> {rm.dsos_count}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <FaUserFriends className="me-1 text-secondary" /> {rm.agents_count}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip id={`tooltip-view-${rm.id}`}>View Details</Tooltip>}
                                >
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="py-0 px-1" 
                                    onClick={() => viewDetails(rm.id)}
                                  >
                                    <FaEye />
                                  </Button>
                                </OverlayTrigger>
                                
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip id={`tooltip-deactivate-${rm.id}`}>
                                    {rm.status === 'Active' ? 'Deactivate' : 'Activate'}
                                  </Tooltip>}
                                >
                                  <Button 
                                    variant={rm.status === 'Active' ? "outline-danger" : "outline-success"}
                                    size="sm" 
                                    className="py-0 px-1"
                                    onClick={() => openDeactivateModal(rm)}
                                  >
                                    {rm.status === 'Active' ? <FaBan /> : <FaCheckCircle />}
                                    <span className="visually-hidden">
                                      {rm.status === 'Active' ? 'Deactivate' : 'Activate'}
                                    </span>
                                  </Button>
                                </OverlayTrigger>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-3">
                            No relationship managers found matching the search criteria
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  
                  {/* Pagination and Entries Per Page Controls */}
                  {rmList.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                      <div className="mb-2">
                        <span>Show </span>
                        <Form.Select
                          style={{ width: 'auto', display: 'inline-block' }}
                          className="mx-2"
                          value={entriesPerPage}
                          onChange={(e) => {
                            setEntriesPerPage(Number(e.target.value));
                            setCurrentPage(1); // Reset to first page when changing entries per page
                          }}
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </Form.Select>
                        <span> entries per page</span>
                      </div>

                      <Pagination className="mb-0">
                        <Pagination.Prev 
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          <FaAngleLeft />
                        </Pagination.Prev>
                        
                        {getVisiblePageNumbers().map(number => (
                          <Pagination.Item 
                            key={number} 
                            active={number === currentPage}
                            onClick={() => paginate(number)}
                          >
                            {number}
                          </Pagination.Item>
                        ))}
                        
                        <Pagination.Next 
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        >
                          <FaAngleRight />
                        </Pagination.Next>
                      </Pagination>

                      <div className="mb-2 text-secondary">
                        Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalItems} entries
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Deactivate/Activate Confirmation Modal */}
      <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedRm?.status === 'Active' ? 'Deactivate' : 'Activate'} Relationship Manager
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            {selectedRm?.status === 'Active' ? (
              <>
                <FaExclamationTriangle size={48} className="text-warning mb-3" />
                <p>Are you sure you want to deactivate <strong>{selectedRm?.name}</strong>?</p>
                <p className="text-muted small">The RM will no longer be able to access the system.</p>
              </>
            ) : (
              <>
                <FaCheckCircle size={48} className="text-success mb-3" />
                <p>Are you sure you want to activate <strong>{selectedRm?.name}</strong>?</p>
                <p className="text-muted small">The RM will regain access to the system.</p>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeactivateModal(false)}>
            Cancel
          </Button>
          <Button 
            variant={selectedRm?.status === 'Active' ? 'danger' : 'success'} 
            onClick={handleDeactivate}
            disabled={statusUpdateLoading}
          >
            {statusUpdateLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Processing...
              </>
            ) : (
              selectedRm?.status === 'Active' ? 'Deactivate' : 'Activate'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RMList;